import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { 
  getBatches, 
  createBatch, 
  updateBatch, 
  deleteBatch, 
  sendBatch 
} from '../../services/emailBatchService';

export interface EmailBatch {
  id: string;
  name: string;
  template: string;
  subject: string;
  recipients: string[];
  scheduledAt: string | null;
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  createdAt: string;
  updatedAt: string;
}

interface EmailBatchState {
  batches: EmailBatch[];
  selectedBatch: EmailBatch | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: EmailBatchState = {
  batches: [],
  selectedBatch: null,
  isLoading: false,
  error: null,
};

export const fetchBatches = createAsyncThunk('emailBatch/fetchBatches', async (_, { rejectWithValue }) => {
  try {
    const response = await getBatches();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch email batches');
  }
});

export const addBatch = createAsyncThunk(
  'emailBatch/addBatch', 
  async (newBatch: Omit<EmailBatch, 'id' | 'status' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const response = await createBatch(newBatch);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create email batch');
    }
  }
);

export const editBatch = createAsyncThunk(
  'emailBatch/editBatch', 
  async ({ id, data }: { id: string; data: Partial<EmailBatch> }, { rejectWithValue }) => {
    try {
      const response = await updateBatch(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update email batch');
    }
  }
);

export const removeBatch = createAsyncThunk('emailBatch/removeBatch', async (id: string, { rejectWithValue }) => {
  try {
    await deleteBatch(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete email batch');
  }
});

export const sendEmailBatch = createAsyncThunk('emailBatch/sendEmailBatch', async (id: string, { rejectWithValue }) => {
  try {
    const response = await sendBatch(id);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to send email batch');
  }
});

const emailBatchSlice = createSlice({
  name: 'emailBatch',
  initialState,
  reducers: {
    setSelectedBatch: (state, action: PayloadAction<EmailBatch | null>) => {
      state.selectedBatch = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBatches.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBatches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.batches = action.payload;
      })
      .addCase(fetchBatches.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(addBatch.fulfilled, (state, action) => {
        state.batches.push(action.payload);
        message.success('Email batch created successfully');
      })
      .addCase(addBatch.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(editBatch.fulfilled, (state, action) => {
        const index = state.batches.findIndex(batch => batch.id === action.payload.id);
        if (index !== -1) {
          state.batches[index] = action.payload;
        }
        message.success('Email batch updated successfully');
      })
      .addCase(editBatch.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(removeBatch.fulfilled, (state, action) => {
        state.batches = state.batches.filter(batch => batch.id !== action.payload);
        message.success('Email batch deleted successfully');
      })
      .addCase(removeBatch.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(sendEmailBatch.fulfilled, (state, action) => {
        const index = state.batches.findIndex(batch => batch.id === action.payload.id);
        if (index !== -1) {
          state.batches[index] = action.payload;
        }
        message.success('Email batch sent successfully');
      })
      .addCase(sendEmailBatch.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      });
  },
});

export const { setSelectedBatch } = emailBatchSlice.actions;
export default emailBatchSlice.reducer; 